'use client';

import { ICartItem } from '@/api/types/cart';
import Button from '@/components/Button';
import Popup from '@/components/Popup';
import PrescriptionTable from '@/components/PrescriptionTable';
import { EPrescriptionType } from '@/utils/enums/goods';
import { useTranslations } from '@/i18n/use-translations';
import { usePriceCalculation } from '../hooks/usePriceCalculation';

interface CartDetailPopupProps {
  open: boolean;
  onClose: () => void;
  cartItemData: ICartItem;
  selected?: boolean;
  itemPromotion?: any;
  itemType?: number;
  currency?: any;
  handleEditPrescription?: () => void;
  handleRemoveLens?: () => void;
  isInline?: boolean; // 是否为内联模式（PC端折叠显示）
}

export default function CartDetailPopup({
  open,
  onClose,
  cartItemData,
  selected = false,
  itemPromotion,
  itemType = 1,
  currency,
  handleEditPrescription,
  handleRemoveLens,
  isInline = false,
}: CartDetailPopupProps) {
  const t = useTranslations('cart.cartDetailPopup');
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');
  // 使用统一的价格计算hook
  const priceCalculation = usePriceCalculation({
    cartItemData,
    currency,
    selected,
    itemPromotion,
  });

  // 从价格计算hook获取所有价格信息
  const allPrices = priceCalculation.getAllPrices();
  const {
    product: productPrice,
    lens: lensPrice,
    index: indexPrice,
    coating: coatingPrice,
    total: totalPrice,
  } = allPrices;

  // 获取其他必要的方法
  const { formatPrice, hasPrescription } = priceCalculation;

  // 检查是否显示移除镜片按钮
  const showRemoveLensButton = () => {
    return (
      itemType !== 1 &&
      cartItemData.product?.prescription_type?.includes(EPrescriptionType.FRAME_ONLY)
    );
  };

  // 获取镜片颜色
  const getLensColor = () => {
    if (cartItemData.lens?.lens_color && cartItemData.lens.lens_color !== 'non') {
      return cartItemData.lens.lens_color;
    }
    return '';
  };

  // 获取镜片品牌名称
  const getLensName = () => {
    if (!cartItemData.lens) return '';

    // 根据lens_display_type判断展示品牌名还是镜片名
    if (cartItemData.lens.lens_display_type === 2) {
      return cartItemData.lens.brand_name || cartItemData.lens.lens_name;
    }
    return cartItemData.lens.lens_name || '';
  };

  // 促销信息
  const hasPromotion =
    selected &&
    itemPromotion &&
    itemPromotion.products_promotions_show === 1 &&
    itemPromotion.over_limit_rule === 1 &&
    itemType === 1;

  const getPromotionMessage = () => {
    if (!hasPromotion) return null;

    if (
      itemPromotion.products_promotions_qty > 0 &&
      cartItemData.qty !== itemPromotion.products_promotions_qty
    ) {
      const getItemText = (qty: number) => {
        if (cartItemData.product?.is_glass === 1) {
          return qty > 1 ? t('frames') : t('frame2');
        }
        return qty > 1 ? t('items') : t('item');
      };

      return (
        <p className="text-[12px]">
          {t('include')} {itemPromotion.products_promotions_qty} {t('discounted')}{' '}
          {getItemText(itemPromotion.products_promotions_qty)}.
        </p>
      );
    }

    if (itemPromotion.products_promotions_qty <= 0) {
      return <p className="text-[12px]">{t('discountUsedUp')}</p>;
    }
    return null;
  };

  const promotionMessage = getPromotionMessage();
  // web端渲染内容
  const renderWebContent = () => (
    <div className="">
      <div className="flex justify-between items-center mb-[12px]">
        <p className="text-[14px] flex-1 mr-[5px]">
          {getLensName()}
          {getLensColor() && <span className="text-[14px]">({getLensColor()})</span>}
        </p>
        <div className="text-right flex-none">
          {lensPrice.price !== lensPrice.sell_price && (
            <span className="text-[#93969e] line-through text-[14px] mr-[6px]">
              <del aria-label={tPriceA11y('srOriginal') + formatPrice(lensPrice.price)}>
                {formatPrice(lensPrice.price)}
              </del>
            </span>
          )}
          <span
            className="text-[14px] text-primary-black"
            aria-label={tPriceA11y('srCurrent') + formatPrice(lensPrice.sell_price)}
          >
            {formatPrice(lensPrice.sell_price)}
          </span>
        </div>
      </div>
      {/* 折射率 */}
      {cartItemData.lens.lens_index && hasPrescription() && (
        <div className="flex justify-between items-center mb-[12px]">
          <div className="text-[14px] flex-1 mr-[5px]">
            {cartItemData.lens.lens_index}
            {cartItemData.lens.brand === 'pro' && (
              <span className="ml-[5px] px-[5px] py-[2px] bg-[#f2ddbc] text-[#3a2107] text-[12px] rounded-[8px]">
                {t('premium')}
              </span>
            )}
          </div>
          <div className="text-right flex-none">
            {indexPrice.price !== indexPrice.sell_price && (
              <span className="text-[#93969e] line-through text-[14px] mr-[6px]">
                <del aria-label={tPriceA11y('srOriginal') + formatPrice(indexPrice.price)}>
                  {formatPrice(indexPrice.price)}
                </del>
              </span>
            )}
            <span
              className="text-[14px] text-primary-black"
              aria-label={tPriceA11y('srCurrent') + formatPrice(indexPrice.sell_price)}
            >
              {formatPrice(indexPrice.sell_price)}
            </span>
          </div>
        </div>
      )}
      {/* 膜层信息 */}
      {cartItemData.coating && (
        <div className="mb-[12px]">
          <div className="flex justify-between items-center">
            <span className="text-[14px] flex-1 mr-[5px]">{cartItemData.coating.coating_name}</span>
            <div className="text-right flex-none">
              {coatingPrice.price !== coatingPrice.sell_price && (
                <span className="text-[#93969e] line-through text-[14px] mr-[6px]">
                  <del aria-label={tPriceA11y('srOriginal') + formatPrice(coatingPrice.price)}>
                    {formatPrice(coatingPrice.price)}
                  </del>
                </span>
              )}
              <span
                className="text-[14px] text-primary-black"
                aria-label={tPriceA11y('srCurrent') + formatPrice(coatingPrice.sell_price)}
              >
                {formatPrice(coatingPrice.sell_price)}
              </span>
            </div>
          </div>
        </div>
      )}
      {/* 处方表格 - 如果有处方数据 */}
      {hasPrescription() && (
        <div className="mb-[16px]">
          <PrescriptionTable
            prescription={{
              prescription: cartItemData.option,
              prescription_type: cartItemData.option.prescription_type,
            }}
            isReadOnly
            onDelete={() => {}}
            onEdit={() => {}}
            onUploadImg={() => {}}
            showPrism
          />
        </div>
      )}
    </div>
  );

  // 渲染内容
  const renderWapContent = () => (
    <div className={`relative max-h-[60vh] overflow-y-auto pb-[20px] px-[14px] pt-[14px]`}>
      {/* 镜框信息 */}
      <div className="mb-[13px]">
        <div className="flex justify-between items-center mb-[3px]">
          <h4 className="font-bb text-[15px]">{t('frame')}</h4>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[13px] flex-1 mr-[5px]">{cartItemData.product?.short_name}</span>
          <div className="text-right flex-none">
            {productPrice.price !== productPrice.sell_price && (
              <span className="text-[#93969e] line-through text-[13px] mr-[6px]">
                <del aria-label={tPriceA11y('srOriginal') + formatPrice(productPrice.price)}>
                  {formatPrice(productPrice.price)}
                </del>
              </span>
            )}
            <span
              className="font-bb text-[13px] text-primary-black"
              aria-label={tPriceA11y('srCurrent') + formatPrice(productPrice.sell_price)}
            >
              {formatPrice(productPrice.sell_price)}
            </span>
          </div>
        </div>
        {cartItemData.product?.color && (
          <div className="text-[13px]">—{cartItemData.product.color}</div>
        )}
      </div>

      {/* 处方信息 */}
      {cartItemData.option?.prescription_type && (
        <div className="mb-[13px]">
          <div className="flex justify-between items-center mt-[13px] mb-[3px]">
            <h4 className="font-bs text-[15px]">{t('prescription')}</h4>
            <span className="text-[13px]">{cartItemData.option.prescription_type_name}</span>
          </div>
          {/* 处方表格 - 如果有处方数据 */}
          {hasPrescription() && (
            <div className="">
              <PrescriptionTable
                prescription={{
                  prescription: cartItemData.option,
                  prescription_type: cartItemData.option.prescription_type,
                }}
                isReadOnly
                onDelete={() => {}}
                onEdit={() => {}}
                onUploadImg={() => {}}
              />
            </div>
          )}
        </div>
      )}

      {/* 镜片信息 */}
      {cartItemData.lens && (
        <div className="mb-[13px]">
          <div className="flex justify-between items-center mb-[3px]">
            <h4 className="font-bs text-[15px]">{t('lens')}</h4>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[13px] flex-1 mr-[5px]">{getLensName()}</span>
            <div className="text-right flex-none">
              {lensPrice.price !== lensPrice.sell_price && (
                <span className="text-[#93969e] line-through text-[13px] mr-[6px]">
                  <del aria-label={tPriceA11y('srOriginal') + formatPrice(lensPrice.price)}>
                    {formatPrice(lensPrice.price)}
                  </del>
                </span>
              )}
              <span
                className="font-bb text-[13px] text-primary-black"
                aria-label={tPriceA11y('srCurrent') + formatPrice(lensPrice.sell_price)}
              >
                {formatPrice(lensPrice.sell_price)}
              </span>
            </div>
          </div>

          {getLensColor() && <div className="text-[13px] mb-[9px]">—{getLensColor()}</div>}

          {/* 折射率 */}
          {cartItemData.lens.lens_index && hasPrescription() && (
            <div className="flex justify-between items-center mb-[13px]">
              <div className="text-[13px] flex-1 mr-[5px]">
                {cartItemData.lens.lens_index}
                {cartItemData.lens.brand === 'pro' && (
                  <span className="ml-[5px] px-[5px] py-[2px] bg-[#f2ddbc] text-[#3a2107] text-[11px] rounded-[8px]">
                    {t('premium')}
                  </span>
                )}
              </div>
              <div className="text-right flex-none">
                {indexPrice.price !== indexPrice.sell_price && (
                  <span className="text-[#93969e] line-through text-[13px] mr-[6px]">
                    <del aria-label={tPriceA11y('srOriginal') + formatPrice(indexPrice.price)}>
                      {formatPrice(indexPrice.price)}
                    </del>
                  </span>
                )}
                <span
                  className="font-bb text-[13px] text-primary-black"
                  aria-label={tPriceA11y('srCurrent') + formatPrice(indexPrice.sell_price)}
                >
                  {formatPrice(indexPrice.sell_price)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 膜层信息 */}
      {cartItemData.coating && (
        <div className="mb-[13px]">
          <div className="flex justify-between items-center mb-[3px]">
            <h4 className="font-bs text-[15px]">{t('coating')}</h4>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[13px] flex-1 mr-[5px]">{cartItemData.coating.coating_name}</span>
            <div className="text-right flex-none">
              {coatingPrice.price !== coatingPrice.sell_price && (
                <span className="text-[#93969e] line-through text-[13px] mr-[6px]">
                  <del aria-label={tPriceA11y('srOriginal') + formatPrice(coatingPrice.price)}>
                    {formatPrice(coatingPrice.price)}
                  </del>
                </span>
              )}
              <span
                className="font-bb text-[13px] text-primary-black"
                aria-label={tPriceA11y('srCurrent') + formatPrice(coatingPrice.sell_price)}
              >
                {formatPrice(coatingPrice.sell_price)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 数量和总价 */}
      <div className="border-t border-[#d0d4db] pt-[21px]">
        <div className="flex justify-between items-center mb-[7px]">
          <span className="text-[13px]">{t('qty')}</span>
          <span className="text-[13px] font-bb">x{cartItemData.qty}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="font-bs text-[15px]">{t('total')}</span>
          <span className="font-bb text-[17px]">{formatPrice(totalPrice.sell_price)}</span>
        </div>
      </div>
    </div>
  );

  // 渲染底部按钮
  const renderBottomButtons = () => (
    <div
      className={`h-[69px] shadow-[0px_0px_10px_0px_rgba(204,204,204,0.5)] flex items-center justify-center`}
    >
      {showRemoveLensButton() && (
        <Button
          onClick={handleRemoveLens}
          sx={{
            border: '1px solid #463AE8',
            color: '#463AE8',
            borderRadius: '50px',
            height: '42px',
            width: '160px',
            marginRight: '16px',
            fontSize: '16px',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
          className="!font-bb"
        >
          {t('removeLenses')}
        </Button>
      )}
      <Button
        onClick={handleEditPrescription}
        sx={{
          backgroundColor: '#463AE8',
          color: 'white',
          borderRadius: '50px',
          height: '42px',
          width: showRemoveLensButton() ? '160px' : '340px',
          fontSize: '16px',
          '&:hover': {
            backgroundColor: '#463AE8',
          },
        }}
        className="!font-bb"
      >
        {t('edit')}
      </Button>
    </div>
  );

  // web端渲染底部按钮
  const renderBottomWebButtons = () => (
    <div className={`flex items-center justify-center mt-[32px] mb-[20px]`}>
      <Button
        onClick={handleEditPrescription}
        className="!font-bb"
        sx={{
          border: '1px solid #282828',
          color: '#282828',
          backgroundColor: 'transparent',
          borderRadius: '2px',
          height: '34px',
          width: '148px',
          fontSize: '16px',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        {t('edit')}
      </Button>

      {showRemoveLensButton() && (
        <Button
          onClick={handleRemoveLens}
          sx={{
            border: '1px solid #282828',
            color: '#282828',
            borderRadius: '2px',
            height: '34px',
            width: '148px',
            marginLeft: '16px',
            fontSize: '16px',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {t('remove')}
        </Button>
      )}
    </div>
  );

  // 内联模式 - 只返回内容，不包装弹窗
  if (isInline) {
    return (
      <div className="">
        {renderWebContent()}
        {renderBottomWebButtons()}
      </div>
    );
  }
  // 移动端使用Popup
  return (
    <Popup
      open={open}
      onClose={onClose}
      position="bottom"
      height="60vh"
      title={t('details')}
      round
      borderRadius={20}
      overlay
      closeOnClickOverlay
      hideCloseIcon={false}
      showDivider
      sx={{
        '& .MuiDrawer-paper': {
          borderRadius: '20px 20px 0 0',
        },
        '& .popup-content': {
          padding: '0px',
        },
      }}
    >
      {renderWapContent()}
      {/* 促销信息 */}
      {hasPromotion && promotionMessage && (
        <div className="bg-[#e9ffbc] w-full h-[32px] flex items-center px-[14px] text-[12px] font-bs">
          {promotionMessage}
        </div>
      )}
      {renderBottomButtons()}
    </Popup>
  );
}
